/**
 * Feature flag keys evaluated through Cloudflare Flagship (`SPEC_feature_flags_TK-12413.md`).
 *
 * Flag *state* lives in Flagship, not here — this file is only the code-side registry: which
 * keys exist, and what each resolves to when evaluation cannot happen at all (binding missing,
 * flag deleted, parse error).
 *
 * Two different "defaults" are in play and must not be conflated:
 * - **this file** ({@link FEATURE_FLAG_FALLBACK}) — returned only when evaluation *fails*.
 * - **Flagship's default variant** — what Flagship serves when a flag is *disabled* or no rule
 *   matches.
 *
 * Both are `false`, for **every** key, with no exceptions: an outage, a deleted flag, a missing
 * binding and a kill-switch all land on "feature off", so no flag can be enabled by accident and
 * nothing has to be reasoned about per key. A feature that must survive Flagship being unreachable
 * does not belong behind a flag.
 */
export const FEATURE_FLAG_KEYS = [
    /**
     * Ecommerce Designer — gates the stationery-purchase half of `/designer/**`
     * (sku → configTemplate → personalize), i.e. the `purchase` value of the `:type` segment.
     *
     * The two shared designer pages route both journeys through one file, so they gate via
     * `featureFlagByParam` rather than a static `featureFlag`. Only `purchase` is mapped today:
     * `handwriting` is deliberately unmapped and therefore ungated until
     * `handwriting-custom-stationery` is re-added (TK-12415 scope decision — see
     * `SPEC_feature_flags_coverage.md` "Deferred flags").
     *
     * **Scoped off in the Capacitor app.** A `platform == "capacitor"` targeting rule serves `off`
     * to the native shell, so the purchase journey is web-only. The rule matches the `platform`
     * attribute sent by `server/utils/resolveFeatureFlags.ts` — that string and the dashboard rule
     * must agree exactly, or the rule silently fails to match and web's value is served to the app
     * too.
     *
     * **Fails closed like every other key.** It used to be the registry's one fail-open exception
     * (`true` on web, `false` in the app) so that a Flagship outage could not take shipped Designer
     * behaviour down. That exception is gone: keeping web on is now entirely the dashboard's job,
     * via a rule or a `true` default variant, and it only holds while Flagship is reachable. A
     * missing binding, a deleted flag or a thrown evaluation now switches the purchase journey off
     * on web as well.
     *
     * **Dashboard config is a `true` default variant plus the platform rule, not the catch-all rule
     * the TK-12413 SPEC prescribes.** A `--add-rule "serve=on"` catch-all is not visible in the
     * Flagship dashboard and is dropped when the flag is saved there — and once dropped the flag
     * reads `false`. A `true` default variant is a first-class field that survives a save.
     *
     * Consequence to know before touching it: the dashboard **Disable** toggle does not kill this
     * flag — disabling serves the default variant, which is `true`, **and it also stops the
     * platform rule from being evaluated**, so disabling turns the purchase journey back ON in the
     * Capacitor app. To kill Ecommerce Designer everywhere, set the default variant to `false`
     * rather than using the toggle.
     */
    'ecommerce',
    /**
     * Designer, as a whole.
     *
     * **Registry-only today — nothing reads this key.** It is registered so
     * `GET /api/feature-flags` returns it and the flag can be created, targeted, and verified
     * end-to-end in Flagship before any code is gated on it: testing the flag pipeline should not
     * require merging a behaviour change first.
     *
     * Note it does **not** currently gate the Designer purchase journey — that is `ecommerce`, on
     * `/designer/{start,personalize}/purchase/**` via `featureFlagByParam`. Decide the split
     * before wiring this up, and if `designer` becomes the umbrella over `ecommerce`, express that
     * through {@link FEATURE_FLAG_PARENTS} rather than an `&&` at the call sites.
     *
     * Nothing reads it, so its value is inert either way — but like every key it fails closed.
     */
    'designer',
    /**
     * Contacts page + bottom-nav tab (TK-13117), renamed from the old `contacts` key.
     *
     * **Gates two surfaces, and less than it looks.** `/contacts` (the index only) via
     * `middleware: ['feature-enabled']`, and the Contacts tab in `BottomNavbar.vue`, so tab and
     * route always agree. Its **6 subpages** (`new`, `[id]/**`) have no `middleware` key at all and
     * render on a direct link with this off; so does the Send-flow contact picker/creator
     * (`ContactDrawerHost.vue`), which can still create contacts and write addresses.
     *
     * **Not enforced on the API (changed in TK-13117).** The 6 contact WRITE endpoints used to call
     * `requireFeatureEnabled(event, 'contacts')` per SPEC A6; that guard was removed from all of
     * them, so this is a UI-visibility flag only. A direct API call reaches the handler regardless
     * of the flag — ownership and auth still apply, the flag does not. Re-adding it is one line per
     * route. Note `requireFeatureEnabled` currently has **no call site anywhere in the repo**.
     *
     * Full per-layer breakdown, including what this could gate but doesn't:
     * `doc/spec/SPEC_feature_flags_coverage.md` §A.
     */
    'contacts-page',
    /**
     * Recent Contacts strip on the Capacitor home screen (TK-12919).
     *
     * **Independent of `contacts-page` since TK-13117** — it used to be a child of the old
     * `contacts` key via {@link FEATURE_FLAG_PARENTS}, so that flag off force-hid the strip. The
     * two are now sibling surface flags, settable in any combination. They were renamed apart on
     * purpose: `contacts` + `contacts-recent` still read as umbrella-and-child, which invites
     * someone to re-add the parent link that was deliberately removed.
     *
     * **Unsupported combination: `contacts-page` off + `contacts-recent` on.** The strip's only
     * action is `navigateToTabStack('contacts')`, and with `contacts-page` off that route is
     * blocked by the middleware and bounces to `/` — so the strip renders but goes nowhere. The
     * parent link used to make this state unreachable; independence is the point of TK-13117, so
     * this is a configuration rule rather than something the code prevents. Turning
     * `contacts-page` off means turning `contacts-recent` off too.
     */
    'contacts-recent',
] as const

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number]

/** Resolved value for every key; always fully populated, never partial. */
export type FeatureFlagMap = Record<FeatureFlagKey, boolean>

/**
 * What a flag reads as when evaluation cannot happen — flag missing, binding absent, evaluation
 * threw. Passed to Flagship as the `defaultValue` on every call, and used to seed the client store.
 *
 * Flat `false`, deliberately not a per-key map. A map invites a `true` entry, and a `true` here is
 * a feature that switches itself **on** whenever Flagship cannot be reached — the opposite of what
 * a kill-switch is for.
 */
export const FEATURE_FLAG_FALLBACK = false

/** {@link FEATURE_FLAG_FALLBACK} for every key, for the paths that need a whole map. */
export const FEATURE_FLAG_DEFAULTS: FeatureFlagMap = Object.fromEntries(
    FEATURE_FLAG_KEYS.map((key) => [key, FEATURE_FLAG_FALLBACK])
) as FeatureFlagMap

/**
 * Parent flag per key — the flag hierarchy, declared **once** here rather than re-expressed at
 * every call site.
 *
 * A child flag is only ever "on" when its whole ancestor chain is on, so a parent acts as a real
 * kill-switch: turning the parent off hides every narrower surface regardless of the child flags.
 * A child's call site therefore needs no `&& parent` — callers read one key and the resolution
 * happens in {@link useFeatureFlagsStore.isEnabled} on the client and `isFeatureEnabled` (hence
 * `requireFeatureEnabled`) on the server, so UI gating and API enforcement agree.
 *
 * **Currently empty (TK-13117).** Its only entry was `contacts-recent` → `contacts`, removed so
 * the two can be switched independently. The mechanism is kept rather than deleted because it has
 * a queued use: `SPEC_feature_flags_coverage.md` §B plans `handwriting-note` and
 * `handwriting-stationery` as children of `handwriting`. Nothing reads a parent today, so
 * `featureFlagChain` returns a single-element chain for every registered key.
 *
 * **Why not check both flags at the call site:** when a dependency is real, expressing it here
 * beats doing it per-component — the dependency would otherwise live in whichever component
 * happened to implement it, and a new call site could silently forget the parent (the `/contacts`
 * page strip did exactly that). Declaring it here makes the relationship greppable.
 *
 * **Rule of thumb for adding an entry:** make a flag a *child* only when there is no state in
 * which you would want it on while the parent is off. If such a state exists, the two flags are
 * siblings on different axes and belong outside this map. TK-13117 is the worked example of the
 * second case — `contacts-recent` was demoted to a sibling once its surface needed switching on
 * its own.
 *
 * Keep the hierarchy at most two levels deep; deeper chains stop being readable.
 */
export const FEATURE_FLAG_PARENTS: Partial<Record<FeatureFlagKey, FeatureFlagKey>> = {}

/**
 * Walks a key up to its root, e.g. `a-child` -> `['a-child', 'its-parent']`.
 *
 * Self-guarding: a cycle introduced by a bad {@link FEATURE_FLAG_PARENTS} edit stops at the
 * already-seen key instead of looping forever.
 */
export function featureFlagChain(key: FeatureFlagKey): FeatureFlagKey[] {
    const chain: FeatureFlagKey[] = []
    let current: FeatureFlagKey | undefined = key
    while (current && !chain.includes(current)) {
        chain.push(current)
        current = FEATURE_FLAG_PARENTS[current]
    }
    return chain
}

/** Type guard for narrowing untrusted input (page meta, query strings) to a known key. */
export function isFeatureFlagKey(value: unknown): value is FeatureFlagKey {
    return typeof value === 'string' && (FEATURE_FLAG_KEYS as readonly string[]).includes(value)
}
